import { useEffect, useMemo, useState } from 'react';
import { PlanBoard, type Picked } from './PlanBoard';
import { SiteDayDialog } from './SiteDayDialog';
import { SiteDayInfoDialog } from './SiteDayInfoDialog';
import { AddSiteDialog } from './AddSiteDialog';
import { buildPlanModel, type SiteTile } from './planModel';
import { EmptyState } from '../../components/ui/Field';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';
import {
  usePlan,
  useAssignSite,
  useRemoveAssignment,
  useRemoveSiteDay,
} from '../../lib/queries';
import { ApiError } from '../../lib/api';
import {
  dayCount,
  formatDate,
  formatRange,
  monthRange,
  shiftRange,
  todayIso,
  weekRange,
  type DateRange,
  type RangeMode,
} from '../../lib/dates';
import { ABSENCE_COLOR, ABSENCE_LABELS } from '../../lib/labels';
import type { SessionUser } from '../../lib/types';
import './plan.css';

/** Was gerade zum Entfernen zur Rückfrage steht. */
interface PendingRemoval {
  tile: SiteTile;
  date: string;
}

/**
 * Der Schichtplan merkt sich seinen Zeitraum über Reiterwechsel hinweg.
 *
 * Beim Wechsel auf einen anderen Reiter wird die Ansicht verworfen — ohne
 * Gedächtnis stünde sie danach wieder auf der aktuellen Woche, obwohl man
 * gerade zwei Wochen weiter geplant hat. Abgelegt im sessionStorage: gilt je
 * Browser-Tab und Arbeitsplatz, ein Neustart des Browsers beginnt frisch.
 */
const VIEW_STORAGE_KEY = 'schichtplan-zeitraum';

interface StoredView {
  mode: RangeMode;
  range: DateRange;
  workdaysOnly: boolean;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function loadStoredView(): StoredView | null {
  try {
    const raw = sessionStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredView>;
    const mode = parsed.mode;
    if (mode !== 'woche' && mode !== 'monat' && mode !== 'frei') return null;
    const from = parsed.range?.from;
    const to = parsed.range?.to;
    if (typeof from !== 'string' || typeof to !== 'string') return null;
    if (!ISO_DATE.test(from) || !ISO_DATE.test(to) || from > to) return null;
    return { mode, range: { from, to }, workdaysOnly: Boolean(parsed.workdaysOnly) };
  } catch {
    return null;
  }
}

/**
 * Der Schichtplan als Tagesbrett: je Tag eine Spalte, darin die Baustellen als
 * Kacheln mit den Namen. So ist auf einen Blick zu sehen, wer an welchem Tag
 * auf welcher Baustelle steht.
 */
export function SchichtplanView({ user }: { user: SessionUser }) {
  const canEdit = user.role !== 'betrachter';
  const toast = useToast();

  // Beim Öffnen dort weitermachen, wo man im Schichtplan aufgehört hat
  // (Wunsch des Nutzers, 12.08.2026) — sonst mit der aktuellen Woche starten.
  const [storedView] = useState(loadStoredView);
  const [mode, setMode] = useState<RangeMode>(storedView?.mode ?? 'woche');
  const [range, setRange] = useState<DateRange>(storedView?.range ?? (() => weekRange(todayIso())));
  // Standard ist die komplette Woche Mo–So — wer nur die Werktage sehen
  // will, setzt den Haken (Wunsch des Nutzers, 06.08.2026; vorher umgekehrt).
  const [workdaysOnly, setWorkdaysOnly] = useState(storedView?.workdaysOnly ?? false);

  useEffect(() => {
    try {
      sessionStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ mode, range, workdaysOnly }));
    } catch {
      // Kein Speicher (z. B. gesperrt) — dann eben ohne Gedächtnis.
    }
  }, [mode, range, workdaysOnly]);

  const [openTile, setOpenTile] = useState<{ siteId: number; date: string } | null>(null);
  const [editTile, setEditTile] = useState<{ siteId: number; date: string } | null>(null);
  const [addDay, setAddDay] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [picked, setPicked] = useState<Picked | null>(null);

  const plan = usePlan(range);
  const assign = useAssignSite();
  const removeAssignment = useRemoveAssignment();
  const removeSiteDay = useRemoveSiteDay();

  const model = useMemo(
    () => (plan.data ? buildPlanModel(plan.data, workdaysOnly) : null),
    [plan.data, workdaysOnly],
  );

  // Escape bricht das Umsetzen ab — auf dem Rechner der schnellste Ausweg.
  useEffect(() => {
    if (!picked) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPicked(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picked]);

  const changeMode = (next: RangeMode) => {
    setMode(next);
    if (next === 'woche') setRange(weekRange(range.from));
    if (next === 'monat') setRange(monthRange(range.from));
  };

  const jumpToToday = () => {
    const today = todayIso();
    if (mode === 'monat') setRange(monthRange(today));
    else if (mode === 'woche') setRange(weekRange(today));
    else setRange({ from: today, to: today });
  };

  const fail = (error: unknown, fallback: string) =>
    toast.error(error instanceof ApiError ? error.message : fallback);

  const handleAssign = (employeeId: number, siteId: number, day: string) => {
    assign.mutate(
      { employeeId, siteId, date: day },
      {
        onSuccess: (result) => {
          if (result.warnings.length > 0) toast.warn('Eingeteilt', result.warnings.join(' '));
        },
        onError: (error) => fail(error, 'Einteilung fehlgeschlagen.'),
      },
    );
  };

  /** Umsetzen = neu eintragen und die alte Einteilung entfernen. */
  const handleMove = (assignmentId: number, siteId: number, day: string) => {
    const source = plan.data?.assignments.find((entry) => entry.id === assignmentId);
    if (!source) return;
    if (source.siteId === siteId && source.date === day) return;

    assign.mutate(
      { employeeId: source.employeeId, siteId, date: day },
      {
        onSuccess: () => removeAssignment.mutate(assignmentId),
        onError: (error) => fail(error, 'Verschieben fehlgeschlagen.'),
      },
    );
  };

  const handleUnassign = (assignmentId: number) => {
    removeAssignment.mutate(assignmentId, {
      onError: (error) => fail(error, 'Austragen fehlgeschlagen.'),
    });
  };

  /** Leere Kacheln gehen ohne Rückfrage; besetzte nur nach Nachfrage. */
  const handleRemoveTile = (tile: SiteTile, date: string) => {
    if (tile.entries.length > 0) {
      setPendingRemoval({ tile, date });
      return;
    }
    confirmRemoval(tile, date);
  };

  const confirmRemoval = (tile: SiteTile, date: string) => {
    removeSiteDay.mutate(
      { siteId: tile.site.id, date },
      {
        onSuccess: (result) => {
          setPendingRemoval(null);
          if (result.removedAssignments > 0) {
            toast.success(
              `„${tile.site.name}" aus dem Tag genommen — ${result.removedAssignments} Einteilung(en) entfernt.`,
            );
          }
        },
        onError: (error) => {
          setPendingRemoval(null);
          fail(error, 'Die Baustelle konnte nicht entfernt werden.');
        },
      },
    );
  };

  if (plan.isLoading || !model || !plan.data) {
    return (
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        Schichtplan wird geladen …
      </div>
    );
  }

  if (plan.data.employees.length === 0) {
    return (
      <div className="view">
        <div className="card">
          <EmptyState
            title="Noch keine Mitarbeiter angelegt"
            description="Der Schichtplan braucht Mitarbeiter und Baustellen. Beides wird in den Reitern „Mitarbeiter“ und „Baustellen“ gepflegt."
          />
        </div>
      </div>
    );
  }

  // Nach dem Neuladen zeigt ein offener Dialog sonst auf einen veralteten Stand.
  const openColumn = openTile
    ? (model.columns.find((column) => column.date === openTile.date) ?? null)
    : null;
  const openSiteTile = openColumn?.tiles.find((tile) => tile.site.id === openTile?.siteId) ?? null;

  const addColumn = addDay ? model.columns.find((column) => column.date === addDay) : undefined;

  // Auch der Bearbeiten-Dialog zeigt nach einem Neuladen den frischen Stand.
  const editSiteTile = editTile
    ? (model.columns
        .find((column) => column.date === editTile.date)
        ?.tiles.find((tile) => tile.site.id === editTile.siteId) ?? null)
    : null;

  return (
    <div className="view-full">
      <header className="plan-toolbar no-print">
        <div className="segmented" role="group" aria-label="Zeitraum-Art">
          <button type="button" aria-pressed={mode === 'woche'} onClick={() => changeMode('woche')}>
            Woche
          </button>
          <button type="button" aria-pressed={mode === 'monat'} onClick={() => changeMode('monat')}>
            Monat
          </button>
          <button type="button" aria-pressed={mode === 'frei'} onClick={() => changeMode('frei')}>
            Zeitraum
          </button>
        </div>

        <div className="plan-stepper">
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setRange(shiftRange(range, mode, -1))}
            aria-label="Zurück"
          >
            ‹
          </button>
          <button type="button" className="btn" onClick={jumpToToday}>
            Heute
          </button>
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setRange(shiftRange(range, mode, 1))}
            aria-label="Weiter"
          >
            ›
          </button>
        </div>

        {mode === 'frei' ? (
          <div className="plan-date-inputs">
            <input
              type="date"
              className="input"
              value={range.from}
              max={range.to}
              onChange={(event) => setRange({ ...range, from: event.target.value })}
              aria-label="Von"
            />
            <span className="faint">bis</span>
            <input
              type="date"
              className="input"
              value={range.to}
              min={range.from}
              onChange={(event) => setRange({ ...range, to: event.target.value })}
              aria-label="Bis"
            />
          </div>
        ) : (
          <span className="plan-range-label">{formatRange(range, mode)}</span>
        )}

        <div className="spacer" />

        <label className="check">
          <input
            type="checkbox"
            checked={workdaysOnly}
            onChange={(event) => setWorkdaysOnly(event.target.checked)}
          />
          Nur Werktage
        </label>

        <button type="button" className="btn" onClick={() => window.print()}>
          Drucken
        </button>
      </header>

      <div className="print-head print-only">
        <h2>Schichtplan · Egger Bau</h2>
        <span>
          {formatRange(range, mode)} · Stand {new Date().toLocaleDateString('de-DE')}
        </span>
      </div>

      <PlanBoard
        model={model}
        canEdit={canEdit}
        picked={picked}
        onPick={setPicked}
        onOpenTile={(siteId, date) => setOpenTile({ siteId, date })}
        onAddSite={setAddDay}
        onEditTile={(tile, date) => setEditTile({ siteId: tile.site.id, date })}
        onRemoveTile={handleRemoveTile}
        onAssign={handleAssign}
        onMove={handleMove}
        onUnassign={handleUnassign}
      />

      <footer className="plan-legend no-print">
        {canEdit ? (
          <span className="plan-legend-hint">
            Kachel antippen zum Einteilen · Name antippen, dann Ziel-Kachel — oder mit der Maus
            ziehen
          </span>
        ) : null}
        <div className="spacer" />
        {(Object.keys(ABSENCE_LABELS) as (keyof typeof ABSENCE_LABELS)[]).map((type) => (
          <span key={type} className="plan-legend-item">
            <span className="swatch" style={{ background: ABSENCE_COLOR[type] }} />
            {ABSENCE_LABELS[type]}
          </span>
        ))}
        <span className="plan-legend-item">
          <span className="plan-conflict">!</span> trotz Abwesenheit
        </span>
        <span>
          {dayCount(range)} Tage · {plan.data.employees.length} Mitarbeiter
        </span>
      </footer>

      {picked ? (
        <div className="plan-pickbar no-print" role="status">
          <span className="plan-pickbar-text">
            <strong>{picked.employee.displayName}</strong> umsetzen — Ziel-Kachel antippen
          </span>
          <button type="button" className="btn" onClick={() => setPicked(null)}>
            Abbrechen
          </button>
        </div>
      ) : null}

      {openSiteTile && openColumn ? (
        <SiteDayDialog
          tile={openSiteTile}
          column={openColumn}
          crews={plan.data.crews}
          employees={plan.data.employees}
          canEdit={canEdit}
          onClose={() => setOpenTile(null)}
        />
      ) : null}

      {editSiteTile && editTile ? (
        <SiteDayInfoDialog
          tile={editSiteTile}
          date={editTile.date}
          onClose={() => setEditTile(null)}
        />
      ) : null}

      {addDay ? (
        <AddSiteDialog
          day={addDay}
          sites={plan.data.sites}
          usedSiteIds={new Set((addColumn?.tiles ?? []).map((tile) => tile.site.id))}
          onClose={() => setAddDay(null)}
        />
      ) : null}

      {pendingRemoval ? (
        <ConfirmDialog
          title="Baustelle aus dem Tag nehmen"
          message={`„${pendingRemoval.tile.site.name}" am ${formatDate(pendingRemoval.date)} aus dem Plan nehmen?`}
          detail={`${pendingRemoval.tile.entries.length} Einteilung(en) an diesem Tag werden dabei entfernt. Die Baustelle selbst bleibt bestehen.`}
          confirmLabel="Aus dem Tag nehmen"
          busy={removeSiteDay.isPending}
          onConfirm={() => confirmRemoval(pendingRemoval.tile, pendingRemoval.date)}
          onCancel={() => setPendingRemoval(null)}
        />
      ) : null}
    </div>
  );
}
